import { HttpClient, HttpErrorResponse, HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import { IContents, IResponseMessage, IApp, IAppResponse, IContentResponse, IPresignedURLResponse, WeatherApiResponse, WeatherData, CityInfo, IAddContentResponse } from '../../models';
import { APIConstants } from '../../common';



@Injectable({
  providedIn: 'root'
})
export class ContentsService {



  constructor(private http: HttpClient) { }

  getAllContents(): Observable<IContentResponse> {
    return this.http.get<IContentResponse>(APIConstants.contents);
  }

  getAllContentsByLimit(limit: number, offset: number, mediaType?: string, search?: string, ordering?: string): Observable<any> {
    let url = '';

    if (search) {
      const category = (!mediaType || mediaType === 'All') ? '' : mediaType;
      url = APIConstants.foldersFiltered + '?limit=' + limit + '&offset=' + offset + '&folder_id=&category=' + encodeURIComponent(category) + '&search=' + encodeURIComponent(search);
    } else {
      if (!mediaType || mediaType === 'All') {
        url = APIConstants.mediaFolders + '?limit=' + limit + '&offset=' + offset;
      } else {
        url = APIConstants.foldersFiltered + '?limit=' + limit + '&offset=' + offset + '&folder_id=&category=' + encodeURIComponent(mediaType);
      }
    }

    if (ordering) {
      url += '&ordering=' + encodeURIComponent(ordering);
    }

    return this.http.get<any>(url);
  }

  getMediaCountList(): Observable<any> {
    return this.http.get<any>(APIConstants.mediaCountList);
  }

  getAllAppsByLimit(limit: number, offset: number, search?: string, ordering?: string): Observable<IContentResponse> {
    let url = APIConstants.contentsApps + '?limit=' + limit + '&offset=' + offset;
    if (search) {
      url += '&search=' + encodeURIComponent(search);
    }
    if (ordering) {
      url += '&ordering=' + encodeURIComponent(ordering);
    }
    return this.http.get<IContentResponse>(url);
  }

  getAllDzynflixItems(): Observable<IContentResponse> {
    return this.http.get<IContentResponse>(APIConstants.dzynflix);
  }

  getAllDzynflixItemsByLimit(limit: number, offset: number, search?: string, ordering?: string): Observable<IContentResponse> {
    let url = APIConstants.dzynflix + '?limit=' + limit + '&offset=' + offset;
    if (search) {
      url += '&search=' + encodeURIComponent(search);
    }
    if (ordering) {
      url += '&ordering=' + encodeURIComponent(ordering);
    }
    return this.http.get<IContentResponse>(url);
  }

  getAllTemplatesItems(): Observable<IContentResponse> {
    return this.http.get<IContentResponse>(APIConstants.templates);
  }

  getAllTemplateItemsByLimit(limit: number, offset: number, search?: string, ordering?: string): Observable<IContentResponse> {
    let url = APIConstants.templates + '?limit=' + limit + '&offset=' + offset;
    if (search) {
      url += '&search=' + encodeURIComponent(search);
    }
    if (ordering) {
      url += '&ordering=' + encodeURIComponent(ordering);
    }
    return this.http.get<IContentResponse>(url);
  }

  deleteTemplateById(template_id: number): Observable<IResponseMessage> {
    return this.http.delete<IResponseMessage>(APIConstants.templates + template_id + '/');
  }

  deleteMultipleTemplates(template_ids: number[]): Observable<IResponseMessage> {
    const options = {
      body: { content_ids: template_ids }
    };
    return this.http.delete<IResponseMessage>(APIConstants.deleteMultipleContents, options);
  }

  addContents(content: FormData): Observable<IAddContentResponse> {
    return this.http.post<IAddContentResponse>(APIConstants.contents, content);
  }

  updateContents(content_id: number, content: FormData): Observable<IContents> {
    return this.http.put<IContents>(APIConstants.contents + content_id + '/', content);
  }

  deleteContentsById(content_id: number): Observable<IResponseMessage> {
    return this.http.delete<IResponseMessage>(APIConstants.contents + content_id + '/');
  }

  deleteMultipleContentsById(content_ids: number[], folder_ids: number[] = []): Observable<IResponseMessage> {
    return this.http.delete<IResponseMessage>(APIConstants.deleteMultipleContents, { body: { content_ids, folder_ids } });
  }

  getContentsById(content_id: number): Observable<IContents> {
    return this.http.get<IContents>(APIConstants.contents + content_id + '/');
  }

  getAppById(app_id: number): Observable<IAppResponse> {
    return this.http.get<IAppResponse>(APIConstants.apps + app_id + '/');
  }

  getAllApps(): Observable<IApp[]> {
    return this.http.get<IApp[]>(APIConstants.apps);
  }

  deleteMultipleContent(content_ids: number[]): Observable<IResponseMessage> {
    const options = {
      body: { content_ids: content_ids } // Pass body here
    };

    return this.http.delete<IResponseMessage>(APIConstants.deleteMultipleContents, options);
  }

  getPresignedURLForS3(file_name: string, file_type: string, file_size: number): Observable<IPresignedURLResponse> {
    return this.http.get<IPresignedURLResponse>(APIConstants.presignedURLForS3
      + '?file_name=' + encodeURIComponent(file_name)
      + '&content_type=' + encodeURIComponent(file_type)
      + '&file_size=' + file_size);
  }

  uploadFileToS3(presignedUrl: string, file: File): Observable<void> {
    return new Observable(observer => {
      const xhr = new XMLHttpRequest();

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          observer.next(xhr.response);
          observer.complete();
        } else {
          observer.error(new HttpErrorResponse({
            error: xhr.response,
            status: xhr.status,
            statusText: xhr.statusText
          }));
        }
      };

      xhr.onerror = () => {
        observer.error(new HttpErrorResponse({
          error: xhr.response,
          status: xhr.status,
          statusText: xhr.statusText || 'Unknown error'
        }));
      };

      xhr.open('PUT', presignedUrl, true);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.send(file);

      return () => xhr.abort();
    });
  }

  uploadFileToS3WithProgress(presignedUrl: string, file: File, contentType?: string): Observable<HttpEvent<void>> {
    return new Observable<HttpEvent<void>>(observer => {
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event: ProgressEvent) => {
        observer.next({
          type: HttpEventType.UploadProgress,
          loaded: event.loaded,
          total: event.total
        });
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          observer.next(new HttpResponse<void>({
            status: xhr.status,
            statusText: xhr.statusText,
            url: presignedUrl
          }));
          observer.complete();
        } else {
          observer.error(new HttpErrorResponse({
            error: xhr.response,
            status: xhr.status,
            statusText: xhr.statusText,
            url: presignedUrl
          }));
        }
      };

      xhr.onerror = () => {
        observer.error(new HttpErrorResponse({
          error: xhr.response,
          status: xhr.status,
          statusText: xhr.statusText || 'Unknown error',
          url: presignedUrl
        }));
      };

      xhr.open('PUT', presignedUrl, true);
      xhr.setRequestHeader('Content-Type', contentType || file.type || 'application/octet-stream');
      xhr.send(file);

      return () => xhr.abort();
    });
  }

  //WEATHER APPS'S DATA

  getCityNames(query: string, limit: number): Observable<CityInfo[]> {
    return this.http.get<CityInfo[]>(APIConstants.weatherAppCities + '?query=' + query + '&limit=' + limit);
  }

  getWeatherInfo(cityName: string): Observable<WeatherData> {
    return this.http.get<WeatherApiResponse>(APIConstants.weatherAppData + '?city=' + encodeURIComponent(cityName)).pipe(
      map(res => ({
        temp: Math.round(res.main.temp),
        feelsLike: Math.round(res.main.feels_like),
        tempMin: Math.round(res.main.temp_min),
        tempMax: Math.round(res.main.temp_max),
        pressure: res.main.pressure,
        description: res.weather?.[0]?.description || '',
        wind: res.wind.speed,
        humidity: res.main.humidity,
        icon: this.getOpenWeatherIcon(res.weather?.[0]?.icon),
        date: new Date(res.dt * 1000).toLocaleDateString(),
        city: res.name,
        country: res.sys.country,
        timezone: res.timezone,
        sunrise: res.sys.sunrise,
        sunset: res.sys.sunset
      })),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  getOpenWeatherIcon(iconCode: string): string {
    const iconMap: Record<string, string> = {
      '01d': '\u2600\uFE0F', '01n': '\uD83C\uDF19',
      '02d': '\uD83C\uDF24\uFE0F', '02n': '\u2601\uFE0F',
      '03d': '\u26C5', '03n': '\u26C5',
      '04d': '\u2601\uFE0F', '04n': '\u2601\uFE0F',
      '09d': '\uD83C\uDF27\uFE0F', '09n': '\uD83C\uDF27\uFE0F',
      '10d': '\uD83C\uDF26\uFE0F', '10n': '\uD83C\uDF26\uFE0F',
      '11d': '\u26C8\uFE0F', '11n': '\u26C8\uFE0F',
      '13d': '\u2744\uFE0F', '13n': '\u2744\uFE0F',
      '50d': '\uD83C\uDF2B\uFE0F', '50n': '\uD83C\uDF2B\uFE0F'
    };
    return iconMap[iconCode] || '\u2601\uFE0F';
    /*
    // Shows emoji; you can return OpenWeatherMap icon URL if preferred
    const iconMap: Record<string, string> = {
      '01d': '☀️', '01n': '🌙',
      '02d': '🌤️', '02n': '🌤️',
      '03d': '⛅', '03n': '⛅',
      '04d': '☁️', '04n': '☁️',
      '09d': '🌧️', '09n': '🌧️',
      '10d': '🌦️', '10n': '🌦️',
      '11d': '⛈️', '11n': '⛈️',
      '13d': '❄️', '13n': '❄️',
      '50d': '🌫️', '50n': '🌫️'
    };
    return iconMap[iconCode] || '❓';
    */
  }

}
